import {useCallback, useMemo} from 'react'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {
  FORKYGRAM_UPLOAD_MAX_IMAGE_SIZE,
  FORKYGRAM_UPLOAD_MAX_RESOLUTION,
  FORKYGRAM_UPLOAD_MAX_VIDEO_DURATION,
  FORKYGRAM_UPLOAD_MAX_VIDEO_SIZE,
  FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION,
  FORKYGRAM_UPLOAD_TYPE,
} from 'consts'
import {
  ForkygramUploadContentBody,
  ForkygramUploadPublishParam,
  ImageExtensionType,
  VideoExtensionType,
} from 'types'
import {handleUploadSingleFileToWasabi} from 'utils/files'
import {showSnackbar} from 'utils/components'
import {useHistory} from 'utils/routes'
import {useSelector, useDispatch} from 'lib/redux'
import {useDidMount} from 'utils/objects'

export function useForkygramUpload({
  stateLoading,
  stateSource,
  form,
  data,
}: ForkygramUploadPublishParam) {
  const history = useHistory()
  const {update} = useDispatch()
  const {translate} = useTranslation()
  const {username} = useSelector('user') || {}
  const setLoading = stateLoading[1]
  const setSrc = stateSource[1]

  const handleRedirect = useCallback(() => {
    history.goBack()
  }, [history])

  const handleNavigateProfile = useCallback(() => {
    history.push('giftshop_profile', {self: true}, username)
  }, [history, username])

  const handleNavigateFotoTree = useCallback(() => {
    history.replace(
      'tree_fototree_detail',
      {
        treeId: data.treeId,
        treeAlias: data.treeAlias,
      },
      data.treeAlias,
    )
  }, [data.treeAlias, data.treeId, history])

  const getContentLink = useCallback(async () => {
    setLoading(true)
    const values = form.current?.getValues()
    if (values && values.file && values.extension) {
      const response = await requestData('forkygram_post_posts_link', {
        useDefaultMessage: true,
        actionType: 'execute',
        data: {
          extension: values.extension,
          width: values.width,
          height: values.height,
          size: values.size,
          tag_id: values.tag.id,
        },
      })
      return typeof response !== 'string' && response.status === 200
        ? response.data.result.link
        : null
    }
    return null
  }, [form, setLoading])

  const publishToWasabi = useCallback(
    async (link: string) => {
      const values = form.current?.getValues()
      if (values && values.file && values.mime) {
        try {
          return handleUploadSingleFileToWasabi(
            values.file,
            link,
            values.mime,
            {
              'Content-Type': values.mime,
              'x-amz-meta-imagewidth': values.width.toString(),
              'x-amz-meta-imageheight': values.height.toString(),
            },
          )
        } catch (error) {
          return false
        }
      }
      return false
    },
    [form],
  )

  const uploadFileMetadata = useCallback(
    async (link: string) => {
      const values = form.current?.getValues()
      if (values && values.file && values.extension) {
        const payload: ForkygramUploadContentBody = {
          caption: values.caption,
          collaborator_member_ids: values.collaborator_member_ids,
          duration: values.duration,
          tag_id: values.tag.id,
          height: values.height,
          width: values.width,
          size: values.size,
          exif: values.exif,
          link,
        }
        const response = await requestData('forkygram_post_posts', {
          useDefaultMessage: true,
          actionType: 'execute',
          data:
            values.thumbnail && values.thumbnail_link
              ? {
                  ...payload,
                  thumbnail: {
                    exif: values.thumbnail.exif,
                    height: values.thumbnail.height,
                    width: values.thumbnail.width,
                    size: values.thumbnail.size,
                    link: values.thumbnail_link,
                  },
                }
              : payload,
        })
        if (typeof response !== 'string' && response.status === 201) {
          const {result} = response.data
          const footprintCount = result.tree?.footprint_count
          if (data.treeId && footprintCount) {
            update('lastTreeState', {footprintCount})
          }
          showSnackbar(
            translate('forkygram:uploadSuccessMessage', {
              context: data.treeId && 'leaf',
            }),
          )
          return result
        }
        if (typeof response !== 'string' && response.status === 429) {
          const {detail} = response.data
          if (detail?.eligible_at) {
            const now = new Date()
            const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes()

            const eligibleDate = new Date(detail.eligible_at)
            const eligibleTimeInMinutes =
              eligibleDate.getHours() * 60 + eligibleDate.getMinutes()

            const remainingMinutes =
              eligibleTimeInMinutes - currentTimeInMinutes
            const remainingTime = Math.floor(remainingMinutes / 60)
            const remainingTimeMinutes = remainingMinutes % 60

            const timeValue =
              remainingTime > 0 ? remainingTime : remainingTimeMinutes
            const timeUnit = translate(
              remainingTime > 0 ? 'global:hour' : 'global:minute',
            ).toLowerCase()

            showSnackbar(
              translate('forkygram:uploadLimitMessage', {
                context: data.treeId && 'leaf',
                value: timeValue,
                unit: timeUnit,
              }),
            )
            return detail
          }
        }
        return false
      }
      return false
    },
    [data.treeId, form, translate, update],
  )

  const getThumbnailLink = useCallback(async () => {
    const values = form.current?.getValues()
    if (values && values.thumbnail) {
      const response = await requestData('forkygram_post_thumbnail', {
        data: {
          extension: values.thumbnail.extension,
          height: values.thumbnail.height,
          width: values.thumbnail.width,
          size: values.thumbnail.size,
        },
      })
      if (typeof response !== 'string' && response.status === 200) {
        return response.data.result.link
      }
      showSnackbar(translate('forkygram:uploadThumbnailFailed'))
      return undefined
    }
    return undefined
  }, [form, translate])

  const uploadThumbnailToWasabi = useCallback(
    async (link: string) => {
      const values = form.current?.getValues()
      if (values && values.thumbnail && values.thumbnail.file) {
        try {
          return handleUploadSingleFileToWasabi(
            values.thumbnail.file,
            link,
            `image/${values.thumbnail.extension}`,
            {
              'Content-Type': `image/${values.thumbnail.extension}`,
              'x-amz-meta-imagewidth': values.thumbnail.width.toString(),
              'x-amz-meta-imageheight': values.thumbnail.height.toString(),
            },
          )
        } catch (error) {
          return false
        }
      }
      return false
    },
    [form],
  )

  const uploadThumbnail = useCallback(async () => {
    const link = await getThumbnailLink()
    if (!link) return false
    const isPublished = await uploadThumbnailToWasabi(link)
    form.current?.setValue('thumbnail_link', link)
    return isPublished
  }, [form, getThumbnailLink, uploadThumbnailToWasabi])

  const uploadPost = useCallback(async () => {
    try {
      const values = form.current?.getValues()
      const link = await getContentLink()
      if (!link) return

      const isPublished = await publishToWasabi(link)

      if (values && values.thumbnail) {
        const isThumbnailPublished = await uploadThumbnail()
        if (!isThumbnailPublished) return
      }
      const isMetadataUploaded = await uploadFileMetadata(link)

      if (isPublished && isMetadataUploaded) {
        update('generalAccountState', {hasLocallyCountedExp: true})
        setSrc(undefined)
        setTimeout(() => {
          data.treeId ? handleNavigateFotoTree() : handleNavigateProfile()
        }, 1000)
      }
    } finally {
      setLoading(false)
    }
  }, [
    data.treeId,
    form,
    getContentLink,
    handleNavigateFotoTree,
    handleNavigateProfile,
    publishToWasabi,
    setLoading,
    setSrc,
    update,
    uploadFileMetadata,
    uploadThumbnail,
  ])

  const handleLoadForm = useCallback(() => {
    if (data.file) {
      const img = data.file
      const filename = data.file.name
      const extension = filename.substring(filename.lastIndexOf('.') + 1) as
        | ImageExtensionType
        | VideoExtensionType
      const mime = img.type
      const {type, size} = img

      if (!FORKYGRAM_UPLOAD_TYPE.includes(type)) {
        handleRedirect()
        showSnackbar(
          img.type.startsWith('image')
            ? translate('forkygram:uploadInvalidImageFormat')
            : translate('forkygram:uploadInvalidVideoFormat'),
        )
        return
      }
      if (type.startsWith('image') && size > FORKYGRAM_UPLOAD_MAX_IMAGE_SIZE) {
        handleRedirect()
        showSnackbar(translate('forkygram:uploadMaxImageSizeMessage'))
        return
      }

      if (type.startsWith('video') && size > FORKYGRAM_UPLOAD_MAX_VIDEO_SIZE) {
        handleRedirect()
        showSnackbar(translate('forkygram:uploadMaxVideoSizeMessage'))
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        const url = reader.result?.toString()

        if (url?.includes('image')) {
          const image = document.createElement('img')
          image.src = url
          image.onload = () => {
            const {width, height} = image

            if (width * height > FORKYGRAM_UPLOAD_MAX_RESOLUTION) {
              setSrc(undefined)
              handleRedirect()
              showSnackbar(
                translate('forkygram:uploadMaxImageResolutionMessage'),
              )
              return
            }
            if (
              width < FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION ||
              height < FORKYGRAM_UPLOAD_MIN_IMAGE_RESOLUTION
            ) {
              setSrc(undefined)
              handleRedirect()
              showSnackbar(
                translate('forkygram:uploadMinImageResolutionMessage'),
              )
              return
            }
            if (form.current) {
              form.current.setValue('extension', extension)
              form.current.setValue('height', height)
              form.current.setValue('width', width)
              form.current.setValue('size', size)
              form.current.setValue('mime', mime)
              form.current.setValue('file', img)
            }
            setSrc(url)
          }
        }
        if (url?.includes('video')) {
          const vid = document.createElement('video')
          vid.src = url

          vid.onloadedmetadata = () => {
            const {videoWidth, videoHeight, duration} = vid

            if (duration > FORKYGRAM_UPLOAD_MAX_VIDEO_DURATION) {
              setSrc(undefined)
              handleRedirect()
              showSnackbar(translate('forkygram:uploadMaxVideoDurationMessage'))
              return
            }
            if (form.current) {
              const vidDuration = duration * 1000
              const finalDuration = Math.floor(vidDuration)

              form.current.setValue('extension', extension)
              form.current.setValue('height', videoHeight)
              form.current.setValue('width', videoWidth)
              form.current.setValue('size', size)
              form.current.setValue('mime', mime)
              form.current.setValue('file', img)
              form.current.setValue('duration', finalDuration)
            }

            setSrc(url)
          }
        }
      }
      reader.readAsDataURL(img)
    }
  }, [data.file, form, handleRedirect, setSrc, translate])

  useDidMount(handleLoadForm)

  return useMemo(() => ({publish: uploadPost}), [uploadPost])
}
